import moment from "moment"
import { PropsWithChildren, useEffect, useState } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { FormType, getDifference } from "../utils"
import { Loading, PrintTransactionModal, TableHeader } from "components"
import { useOptionalCountDetailContext } from "../contexts"
import { convertNumber, useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById } from "utils"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useSupplier } from "pages/Purchase/Transaction/components/Supplier/utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"

export function PrintCountInventory(params: { toggle: () => void }): JSX.Element {
  const { control } = useFormContext<FormType>()
  const countdetail = useWatch({ control, name: "countdetail" })
  const transaction_date = useWatch({ control, name: "transaction_date" })
  const trx_code = useWatch({ control, name: "trx_code" })

  const getCountDetail = useOptionalCountDetailContext()

  const setting = useSubdomainSetting()
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()
  
  const { userTokenData } = useHandleAuth()
  const userToken = userTokenData()

  const [address, setAddress] = useState<string>("")

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }

  const isDataEmpty = <tr><td colSpan={7}><Loading errorText="No data available" /></td></tr>

  return (
    <PrintTransactionModal
      isLoading={company.isLoading || supplier.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading}
      toggle={params.toggle}
    >
      <section className="flex flex-col gap-3">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems uppercase label="ADDRESS" text={getAddress()} />
                <InfoItems label="PHONE" text={setting.data?.phone!} />
                <InfoItems label="EMAIL" text={setting.data?.email!} />
                <InfoItems label="WEBSITE" text={setting.data?.website!} />
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">
              COUNT INVENTORY
            </div>

            <table>
              <tbody className="font-bold ">
                <InfoItems width={220} textRight label="DATE" text={moment(transaction_date).format("DD/MM/YYYY")} />
                <InfoItems width={220} textRight label="COUNT INVENTORY NO" text={trx_code} />
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <TableHeader>
            <tr>
              <th>NO</th>
              <th>ITEM</th>
              <th>DESCRIPTION</th>
              <th>UNIT</th>
              <th>SOH</th>
              <th>COUNT</th>
              <th>DIFFERENT</th>
            </tr>
          </TableHeader>
          <tbody>
            {!countdetail.length ? isDataEmpty : (
              countdetail.map((item, key) => {
                const dataCount = getCountDetail?.data?.countdetail?.find((i) => i?.product_id === item?.product_id)

                return (
                  <Controller
                    key={key}
                    name={`countdetail.${key}`}
                    control={control}
                    render={({ field }) => (
                      <tr>
                        <td className="text-center">{key + 1}</td>
                        <td>{dataCount?.product_barcode}</td>
                        <td>{dataCount?.product_name}</td>
                        <td>{dataCount?.mr_unit_name}</td>
                        <td className="text-right">{convertNumber(item.soh_count).intoCurrency}</td>
                        <td className="text-right">{convertNumber(item.real_count).intoCurrency}</td>
                        <td className="text-right">
                            {convertNumber(getDifference(item.soh_count, field.value.real_count)).intoNormalAmount}
                        </td>
                      </tr>
                    )}
                  />
                )
              })
            )}
          </tbody>
        </table>
      </section>
    </PrintTransactionModal>
  )
}

type InfoItemsType = {
  label: string
  text: string
  uppercase?: boolean
  textRight?: boolean
  width?: number
}
const InfoItems = (props: PropsWithChildren<InfoItemsType>) => (
  <tr>
    <td width={props?.width ? props?.width : 120} className="pr-2 uppercase flex justify-between">
      <div>{props?.label}</div>
      <div>:</div>
    </td>
    <td className={props?.uppercase ? `uppercase` : props?.textRight ? "text-right" : ""}>{props?.text || "-"}</td>
  </tr>
)